<template>
  <div class="generate-report">
    <div class="report-input-group">
      <label>Select Report:</label>
      <dropdown-select
        class="bordered"
        :menu-items="reports"
        @select-item="setSelectedReport($event)"
        @clear-item="onClearItem('REPORT')"
      />
    </div>
    <div v-if="selectedReport && isValidReportField('Start Date')" class="report-input-group">
      <label>Start Date</label>
      <form-date-picker type="bordered" :value="reportStartDate" @change="onStartDateSelect" />
    </div>
    <div v-if="selectedReport && isValidReportField('Date Range')" class="report-input-group">
      <label>Date Range</label>
      <form-date-picker
        type="bordered"
        :value="reportStartDateRange"
        @change="onStartDateRangeSelect"
      />
      <span> To </span>
      <form-date-picker
        type="bordered"
        :value="reportEndDateRange"
        @change="onEndDateRangeSelect"
      />
    </div>
    <div v-if="selectedReport && isValidReportField('Branch')" class="report-input-group">
      <label>{{ $t('assignment.labels.formLabels.branch') }}</label>
      <DropdownSelect
        :menu-items="branchNames"
        class="bordered"
        @select-item="onBranchSelect($event)"
        @clear-item="onClearItem('BRANCH')"
      />
    </div>
    <div v-if="selectedReport && isValidReportField('Client')" class="report-input-group">
      <label>{{ $t('reports.labels.client') }}</label>
      <DropdownSelect
        :menu-objects="clients"
        :menu-items="clients.map(c => c.name)"
        class="bordered"
        @select-item="onClientSelect($event)"
        @clear-item="onClearItem('CLIENT')"
      />
    </div>
    <div class="report-input-group">
      <label>&nbsp;</label>
      <tol-button
        id="run-btn"
        button-type="save"
        label="Run Report"
        class="report-action"
        @click="doReportAction()"
      >
      </tol-button>
    </div>
    <update-modal v-if="isProgressing">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
  </div>
</template>
<script>
import DropdownSelect from '@/app/components/DropdownSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import { AppGetters, AppActions } from '@/app/app.keys';
import { ReportsGetters, ReportsActions } from '../reports.keys';
import TolButton from '@/app/components/TolButton';
import format from 'date-fns/format';
import { dateUtils } from '@/mixins/date-utils';
import { userUtils } from '@/mixins/user-utils';
import UpdateModal from '@/app/components/UpdateModal';

export default {
  name: 'GenerateReport',
  components: {
    DropdownSelect,
    FormDatePicker,
    TolButton,
    UpdateModal
  },
  mixins: [dateUtils, userUtils],
  data() {
    return {
      selectedReport: '',
      reportInputFields: {
        start_date: null,
        start_date_range: null,
        end_date_range: null,
        branch: '',
        client_reference_number: ''
      }
    };
  },
  computed: {
    isProgressing() {
      return this.$store.getters[ReportsGetters.IsReportInProgress];
    },
    reports() {
      return this.isSystemAdmin
        ? this.$store.getters[AppGetters.Reports]
        : this.$store.getters[ReportsGetters.ReportsForUser].map(report => report.name);
    },
    availableInputFields() {
      return this.$store.getters[ReportsGetters.Fields];
    },
    clients() {
      return this.$store.getters[ReportsGetters.ReportClients];
    },
    reportEntitlements() {
      return this.$store.getters[ReportsGetters.ReportEntitlements];
    },
    branchNames() {
      return this.isSystemAdmin
        ? this.$store.getters[AppGetters.BranchNames]
        : this.$store.getters[AppGetters.BranchNames].filter(branch => {
            let userEntitlement = this.reportEntitlements.filter(report => {
              if (report.name == this.selectedReport && report.parameter_name == 'Branch') {
                return report;
              }
            });
            if (
              userEntitlement &&
              userEntitlement[0].report_options &&
              userEntitlement[0].report_options.split(',').includes(branch)
            ) {
              return branch;
            }
          });
    },
    reportStartDate() {
      return this.reportInputFields.start_date
        ? this.dateFromString(this.reportInputFields.start_date)
        : null;
    },
    reportStartDateRange() {
      return this.reportInputFields.start_date_range
        ? this.dateFromString(this.reportInputFields.start_date_range)
        : null;
    },
    reportEndDateRange() {
      return this.reportInputFields.end_date_range
        ? this.dateFromString(this.reportInputFields.end_date_range)
        : null;
    }
  },
  mounted() {
    if (!this.$store.getters[AppGetters.BranchNames].length) {
      this.$store.dispatch(AppActions.RetrieveGlobalValues);
    }
    this.$store.dispatch(ReportsActions.RetrieveReportsForUser);
    this.$store.dispatch(ReportsActions.RetrieveUserReportEntitlements);
  },
  methods: {
    onClearItem(item) {
      switch (item) {
        case 'CLIENT':
          this.reportInputFields.client_reference_number = '';
          break;
        case 'REPORT':
          this.selectedReport = '';
          break;
        case 'BRANCH':
          this.reportInputFields.branch = '';
          break;
      }
    },
    setSelectedReport(report) {
      this.selectedReport = report;
    },
    isValidReportField(field) {
      return (
        this.availableInputFields[this.selectedReport] &&
        this.availableInputFields[this.selectedReport].includes(field)
      );
    },
    onStartDateSelect(evt) {
      this.reportInputFields.start_date = this.formatDate(evt);
    },
    onStartDateRangeSelect(evt) {
      this.reportInputFields.start_date_range = this.formatDate(evt);
    },
    onEndDateRangeSelect(evt) {
      this.reportInputFields.end_date_range = this.formatDate(evt);
    },
    onBranchSelect(evt) {
      this.reportInputFields.branch = evt;
    },
    onClientSelect(evt) {
      this.reportInputFields.client_reference_number = evt.client_reference_number;
    },
    formatDate(date) {
      return date ? format(date, 'YYYY-MM-DD') : null;
    },
    doReportAction() {
      this.$store.dispatch(ReportsActions.RunReport, {
        report: this.selectedReport,
        value: this.reportInputFields
      });
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
@import '@/app/styles/action-buttons.scss';

.report-input-group {
  display: flex;
  width: 50%;
  align-items: center;
  margin-bottom: 20px;

  .form-date-picker {
    margin: 0;
  }

  span {
    display: inline-block;
    margin: 0 5px;
  }

  .popover-container {
    display: flex;
    flex: 1;
  }
}

label {
  width: 150px;
  text-align: left;
}

.bordered {
  flex: 1;
}
</style>
